import { createEmptyScoreSheet } from "./scoring/totals";
import {
  type InitGameOptions,
  YACHT_MAX_PLAYERS,
  YACHT_MIN_PLAYERS,
  type YachtDice,
  type YachtGameState,
  type YachtHeldDice,
  type YachtPlayer,
  type YachtPlayerConfig,
  type YachtTurnMode,
  type YachtTurnModeConfig,
  type YachtTurnState,
} from "./types";
import { createTurnRngState, normalizeSeed } from "./utils/seeded-random";

const EMPTY_DICE: YachtDice = [null, null, null, null, null];
const EMPTY_HELD: YachtHeldDice = [false, false, false, false, false];

function normalizePlayers(options: InitGameOptions): readonly YachtPlayer[] {
  const playerConfigs =
    options.players ??
    options.playerIds?.map((playerId): YachtPlayerConfig => ({ playerId })) ??
    [];

  if (
    playerConfigs.length < YACHT_MIN_PLAYERS ||
    playerConfigs.length > YACHT_MAX_PLAYERS
  ) {
    throw new Error(
      `Yacht requires ${YACHT_MIN_PLAYERS}-${YACHT_MAX_PLAYERS} players`,
    );
  }

  const ids = playerConfigs.map((player) => player.playerId.trim());
  if (ids.some((id) => id.length === 0)) {
    throw new Error("Player IDs must be non-empty");
  }

  const deduped = new Set(ids);
  if (deduped.size !== ids.length) {
    throw new Error("Player IDs must be unique");
  }

  return playerConfigs.map((player, index) => ({
    playerId: player.playerId.trim(),
    displayName:
      player.displayName?.trim() ||
      player.playerId.trim() ||
      `Player ${index + 1}`,
  }));
}

export function resolveTurnMode(
  playerCount: number,
  configuredMode: YachtTurnModeConfig = "auto",
): YachtTurnMode {
  if (configuredMode === "auto") {
    return playerCount <= 6 ? "SEQUENTIAL" : "SIMULTANEOUS";
  }

  if (configuredMode === "SEQUENTIAL" && playerCount > 6) {
    throw new Error("Sequential Yacht is valid only for 2-6 players");
  }

  if (configuredMode === "SIMULTANEOUS" && playerCount < 4) {
    throw new Error(
      "Yacht Race simultaneous mode is valid only for 4-12 players",
    );
  }

  return configuredMode;
}

export function countFilledCategories(
  state: Pick<YachtGameState, "scoreSheetsByPlayerId">,
  playerId: string,
): number {
  return Object.keys(state.scoreSheetsByPlayerId[playerId]?.entries ?? {})
    .length;
}

export function createFreshTurnSlot(
  gameSeed: string,
  playerId: string,
  sheetRound: number,
  turnNumber: number,
): YachtTurnState {
  return {
    turnNumber,
    sheetRound,
    playerId,
    dice: EMPTY_DICE,
    held: EMPTY_HELD,
    rollCount: 0,
    status: "OPEN",
    rngState: createTurnRngState(gameSeed, playerId, sheetRound),
  };
}

export function initGame(options: InitGameOptions): YachtGameState {
  const players = normalizePlayers(options);
  const turnMode = resolveTurnMode(players.length, options.turnMode);
  const gameSeed = normalizeSeed(options.seed);
  const seatingOrder = players.map((player) => player.playerId);
  const scoreSheetsByPlayerId = Object.fromEntries(
    seatingOrder.map((playerId) => [playerId, createEmptyScoreSheet(playerId)]),
  );
  const initialTurnPlayerIds =
    turnMode === "SIMULTANEOUS" ? seatingOrder : seatingOrder.slice(0, 1);
  const turnSlotsByPlayerId = Object.fromEntries(
    initialTurnPlayerIds.map((playerId) => [
      playerId,
      createFreshTurnSlot(gameSeed, playerId, 1, 1),
    ]),
  );

  return {
    gameId: options.gameId ?? `yacht-${seatingOrder.join("-")}`,
    phase: "ROLLING",
    turnMode,
    gameSeed,
    sheetRound: 1,
    turnNumber: 1,
    players,
    seatingOrder,
    turnSlotsByPlayerId,
    scoreSheetsByPlayerId,
  };
}
